import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Volume2, VolumeX } from 'lucide-react';

// -------------------------------------------------------------
// SMG "THE REEL" — v2 Cinematic Trailer
//   Phase -1: Gate  |  0: pre-roll letterbox  |  1: Cold Open
//   2: VO1 Manifesto  |  3: Craft Montage (abstract)  |  4: VO2 Manifesto
//   5: AI · CULTURE · REVENUE  |  6: Logo Climax  |  7: Finale  |  8: Done
// -------------------------------------------------------------

const SMG_LOGO =
  'https://customer-assets.emergentagent.com/job_cinema-hub-693/artifacts/jhtdaos4_SMG%20ALL%20LOGO%20PNG.png';

// Cold open — abstract cinematic mood reel (no products, no logos)
// Sequence follows a narrative: light → motion → depth → identity → scale → craft
const COLD_OPEN = [
  { src: 'https://images.unsplash.com/photo-1487956382158-bb926046304a?w=1920&q=80', label: 'PRESENCE' },       // bokeh light
  { src: 'https://images.unsplash.com/photo-1493857671505-72967e2e2760?w=1920&q=80', label: 'MOMENTUM' },       // silhouette in fog
  { src: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1920&q=80', label: 'ENERGY' },         // concert
  { src: 'https://images.unsplash.com/photo-1519692933481-e162a57d6721?w=1920&q=80', label: 'SIGNAL' },         // stage lights
  { src: 'https://images.unsplash.com/photo-1449034446853-66c86144b0ad?w=1920&q=80', label: 'SCALE' },          // city aerial
  { src: 'https://images.unsplash.com/photo-1516684732162-798a0062be99?w=1920&q=80', label: 'CRAFT' },          // motion blur
];

// Backdrop for VO1 — "generational / timeless" mood behind "Every generation..."
const VO1_BG = 'https://images.unsplash.com/photo-1519925610903-381054cc2a1c?w=1920&q=80';
// Backdrop for VO2 — a "culture / crowd / movement" mood behind "They build culture"
const VO2_BG = 'https://images.unsplash.com/photo-1524650359799-842906ca1c06?w=1920&q=80';

// Rapid subliminal flashes behind AI. / CULTURE. / REVENUE.
const TRI_FLASHES = {
  AI: [
    'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1600&q=70',   // circuitry
    'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1600&q=70',   // matrix/data
    'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1600&q=70',      // code
  ],
  CULTURE: [
    'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1600&q=70',   // crowd
    'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1600&q=70',   // art gallery
    'https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=1600&q=70',   // creator/photographer
  ],
  REVENUE: [
    'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1600&q=70',   // skyline
    'https://images.unsplash.com/photo-1519677100203-a0e668c92439?w=1600&q=70',   // luxury/wealth
    'https://images.unsplash.com/photo-1517032549954-e7dcbea78c48?w=1600&q=70',   // signature product
  ],
};

// Mid-montage between VO1 and VO2 — abstract "what we build" (no brand names)
// Themes align with SMG's craft: FILM, LIVE, IDENTITY, DROPS, STORY
const THEMES = [
  { key: 'film',     label: '01 · FILM',      accent: '#111111',
    img: 'https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=1920&q=80' }, // photographer / cameras
  { key: 'live',     label: '02 · LIVE',      accent: '#7A0EFF',
    img: 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=1920&q=80' }, // concert stage
  { key: 'identity', label: '03 · IDENTITY',  accent: '#FF3D2E',
    img: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1920&q=80' }, // fashion editorial
  { key: 'story',    label: '04 · STORY',     accent: '#00263A',
    img: 'https://images.unsplash.com/photo-1489599512549-9e69cabe22ce?w=1920&q=80' }, // cinema / film reels
  { key: 'reach',    label: '05 · REACH',     accent: '#F4EFE4', labelDark: true,
    img: 'https://images.unsplash.com/photo-1471478331149-c72f17e33c73?w=1920&q=80' }, // crowd / audience
];

// Timings synced to actual VO durations
const VO1_SLAMS = [
  { t: 100,  text: 'EVERY GENERATION' },
  { t: 850,  text: 'A FEW BRANDS' },
  { t: 1500, text: 'CHANGE THE GAME.' },
];
const VO2_SLAMS = [
  { t: 100,  text: "THEY DON'T CHASE ATTENTION." },
  { t: 1300, text: 'THEY CREATE IT.' },
  { t: 2500, text: "THEY DON'T SELL CONTENT." },
  { t: 3750, text: 'THEY BUILD CULTURE.' },
];
const VO3_TRI_TIMINGS = [150, 750, 1350]; // AI. / CULTURE. / REVENUE.
const VO3_LOGO_AT = 1950;                  // "This is SMG Media Group"

// -------- Web-Audio music bed --------
function useMusicBed() {
  const ctxRef = useRef(null);
  const nodesRef = useRef([]);

  const start = () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    ctxRef.current = ctx;
    const master = ctx.createGain();
    master.gain.value = 0.35;
    master.connect(ctx.destination);
    const sub = ctx.createOscillator();
    const subG = ctx.createGain();
    sub.type = 'sine';
    sub.frequency.value = 42;
    subG.gain.value = 0;
    subG.gain.linearRampToValueAtTime(0.6, ctx.currentTime + 1.5);
    sub.connect(subG).connect(master);
    sub.start();
    nodesRef.current.push({ osc: sub, gain: subG });
  };

  const impact = (freq = 60, dur = 0.4) => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq * 3, now);
    osc.frequency.exponentialRampToValueAtTime(freq, now + dur);
    g.gain.value = 0;
    g.gain.linearRampToValueAtTime(0.55, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    osc.connect(g).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + dur);
  };

  // Clapperboard CLACK — sharp transient noise burst
  const clack = () => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.15, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 3);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = 0.9;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 1200;
    src.connect(hp).connect(g).connect(ctx.destination);
    src.start(now);
    // low thump under it
    const osc = ctx.createOscillator();
    const og = ctx.createGain();
    osc.frequency.value = 80;
    og.gain.value = 0.7;
    og.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
    osc.connect(og).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.25);
  };

  // Cinematic whoosh transition
  const whoosh = () => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.6, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      d[i] = (Math.random() * 2 - 1) * (i / d.length) * (1 - i / d.length) * 2;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = 0.5;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(400, now);
    bp.frequency.exponentialRampToValueAtTime(3000, now + 0.6);
    src.connect(bp).connect(g).connect(ctx.destination);
    src.start(now);
  };

  const stop = () => {
    nodesRef.current.forEach((n) => {
      try {
        n.gain.gain.cancelScheduledValues(0);
        n.gain.gain.setValueAtTime(0, ctxRef.current.currentTime);
        n.osc.stop(ctxRef.current.currentTime + 0.05);
      } catch { /* noop */ }
    });
    nodesRef.current = [];
    if (ctxRef.current) { try { ctxRef.current.close(); } catch { /* noop */ } }
    ctxRef.current = null;
  };

  // Gradual gain ramp for cinematic fade-out under the finale
  const fadeOut = (durMs = 1500) => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;
    nodesRef.current.forEach((n) => {
      try {
        n.gain.gain.cancelScheduledValues(now);
        n.gain.gain.setValueAtTime(n.gain.gain.value, now);
        n.gain.gain.exponentialRampToValueAtTime(0.0001, now + durMs / 1000);
      } catch { /* noop */ }
    });
  };

  return { start, impact, clack, whoosh, fadeOut, stop };
}

export default function Intro({ onFinish }) {
  const [phase, setPhase] = useState(-1);
  const [slam, setSlam] = useState(0);
  const [openIdx, setOpenIdx] = useState(-1);
  const [themeIdx, setThemeIdx] = useState(-1);
  const [triWord, setTriWord] = useState(-1);
  const [triFlash, setTriFlash] = useState(0);
  const [flash, setFlash] = useState(false);
  const [muted, setMuted] = useState(false);
  const [tc, setTc] = useState('00:00:00:00');
  const [letterbox, setLetterbox] = useState(false);

  const audioRefs = useRef({ vo1: null, vo2: null, vo3: null });
  const timersRef = useRef([]);
  const rafRef = useRef(null);
  const startTimeRef = useRef(0);
  const phaseRef = useRef(-1);
  const music = useMusicBed();

  const addTimer = (fn, delay) => {
    const id = setTimeout(fn, delay);
    timersRef.current.push(id);
    return id;
  };
  const doFlash = () => { setFlash(true); addTimer(() => setFlash(false), 70); };
  const setP = (p) => { phaseRef.current = p; setPhase(p); };

  // --------- MAIN SEQUENCE ---------
  const startSequence = () => {
    setP(0); // brief black + letterbox close (no slate)
    startTimeRef.current = performance.now();
    music.start();
    setLetterbox(true);

    // TC counter
    const rafTick = () => {
      const ms = Math.floor(performance.now() - startTimeRef.current);
      const ff = Math.floor((ms / (1000 / 24)) % 24).toString().padStart(2, '0');
      const ss = Math.floor((ms / 1000) % 60).toString().padStart(2, '0');
      const mm = Math.floor((ms / 60000) % 60).toString().padStart(2, '0');
      setTc(`00:${mm}:${ss}:${ff}`);
      rafRef.current = requestAnimationFrame(rafTick);
    };
    rafRef.current = requestAnimationFrame(rafTick);

    // Short pre-roll: letterbox slides in, sub-rumble builds → cold open at 500ms
    addTimer(() => { music.whoosh(); }, 200);
    addTimer(() => runColdOpen(), 500);
  };

  // --- Phase 1: COLD OPEN MONTAGE (~2000ms, 6 images × 330ms) ---
  const runColdOpen = () => {
    setP(1);
    music.whoosh();
    setOpenIdx(0);
    doFlash();
    COLD_OPEN.forEach((_, i) => {
      if (i === 0) return;
      addTimer(() => {
        setOpenIdx(i);
        doFlash();
        music.impact(55 + i * 5, 0.22);
      }, i * 330);
    });
    // -> Phase 2 VO1 at end of cold open
    addTimer(() => runVO1(), COLD_OPEN.length * 330 + 100);
  };

  // --- Phase 2: VO1 MANIFESTO ---
  const runVO1 = () => {
    setP(2); setSlam(0); doFlash(); music.impact(45, 0.6);
    const a = audioRefs.current.vo1;
    if (a) { a.currentTime = 0; a.play().catch(() => {}); }
    VO1_SLAMS.forEach((s, i) => {
      addTimer(() => { setSlam(i); doFlash(); music.impact(85, 0.3); }, s.t);
    });
    const onEnd = () => { a.removeEventListener('ended', onEnd); runVO2(); };
    if (a) a.addEventListener('ended', onEnd);
    // Safety
    addTimer(() => { if (phaseRef.current === 2) runVO2(); }, 3200);
  };

  // --- Phase 3 removed: themes now play as backdrops for each VO2 slam ---

  // --- Phase 4: VO2 MANIFESTO — each slam gets its own theme backdrop ---
  const runVO2 = () => {
    setP(4); setSlam(0); setThemeIdx(0); doFlash(); music.impact(50, 0.6);
    music.whoosh();
    const b = audioRefs.current.vo2;
    if (b) { b.currentTime = 0; b.play().catch(() => {}); }
    VO2_SLAMS.forEach((s, i) => {
      addTimer(() => {
        setSlam(i);
        setThemeIdx(i); // cycle theme backdrop with each slam
        doFlash();
        music.impact(90, 0.3);
      }, s.t);
    });
    const onEnd = () => { b.removeEventListener('ended', onEnd); runTriptych(); };
    if (b) b.addEventListener('ended', onEnd);
    addTimer(() => { if (phaseRef.current === 4) runTriptych(); }, 5300);
  };

  // --- Phase 5: TRIPTYCH (AI · CULTURE · REVENUE) synced to VO3 ---
  const runTriptych = () => {
    setP(5); setTriWord(0); setTriFlash(0); doFlash(); music.impact(95, 0.4);
    const c = audioRefs.current.vo3;
    if (c) { c.currentTime = 0; c.play().catch(() => {}); }

    // Rapid image flashes behind each word (60ms cycle)
    const flashInt = setInterval(() => setTriFlash((f) => (f + 1) % 3), 90);
    timersRef.current.push({ then: () => clearInterval(flashInt) });

    addTimer(() => { setTriWord(1); doFlash(); music.impact(100, 0.35); }, VO3_TRI_TIMINGS[1] - VO3_TRI_TIMINGS[0]);
    addTimer(() => { setTriWord(2); doFlash(); music.impact(110, 0.4); }, VO3_TRI_TIMINGS[2] - VO3_TRI_TIMINGS[0]);

    // Phase 6: logo climax when "This is SMG..." starts
    addTimer(() => {
      clearInterval(flashInt);
      runLogoClimax(c);
    }, VO3_LOGO_AT - VO3_TRI_TIMINGS[0]);
  };

  // --- Phase 6: LOGO CLIMAX (during "This is SMG Media Group") ---
  const runLogoClimax = (voAudio) => {
    setP(6); doFlash(); music.impact(38, 1.1); music.whoosh();
    const onEnd3 = () => {
      voAudio.removeEventListener('ended', onEnd3);
      runFinale();
    };
    if (voAudio) voAudio.addEventListener('ended', onEnd3);
    addTimer(() => { if (phaseRef.current === 6) runFinale(); }, 1400);
  };

  // --- Phase 7: FINALE CARD, gradual fade to homepage ---
  const runFinale = () => {
    setP(7); doFlash();
    // Music: begin gradual fade under the finale card
    music.fadeOut(1600);
    // Hold finale a beat, then begin the opacity fade + soft letterbox reveal
    addTimer(() => {
      setP(8); // triggers opacity fade via CSS (1500ms)
      setLetterbox(false);
    }, 1500);
    // Only fully unmount / hand off after the fade completes
    addTimer(() => {
      music.stop();
      onFinish?.();
    }, 3000);
  };

  useEffect(() => {
    // Preload all imagery so nothing is blank when its phase hits
    const urls = [
      ...COLD_OPEN.map((c) => c.src),
      VO1_BG, VO2_BG,
      ...THEMES.map((c) => c.img).filter(Boolean),
      ...TRI_FLASHES.AI, ...TRI_FLASHES.CULTURE, ...TRI_FLASHES.REVENUE,
      SMG_LOGO,
    ];
    urls.forEach((u) => { const im = new Image(); im.src = u; });

    return () => {
      timersRef.current.forEach((t) => {
        if (typeof t === 'number') clearTimeout(t);
        else if (t && t.then) t.then();
      });
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      music.stop();
    };
  }, []);

  const handleGate = (opts = { muted: false }) => {
    setMuted(opts.muted);
    if (opts.muted) {
      [audioRefs.current.vo1, audioRefs.current.vo2, audioRefs.current.vo3].forEach((a) => { if (a) a.muted = true; });
    }
    startSequence();
  };

  const handleSkip = () => {
    music.stop();
    timersRef.current.forEach((t) => {
      if (typeof t === 'number') clearTimeout(t);
      else if (t && t.then) t.then();
    });
    [audioRefs.current.vo1, audioRefs.current.vo2, audioRefs.current.vo3].forEach((a) => {
      if (a) { try { a.pause(); } catch { /* noop */ } }
    });
    setP(8);
    onFinish?.();
  };

  const activeTheme = THEMES[themeIdx];
  const activeOpen = COLD_OPEN[openIdx];

  return (
    <div
      className={`fixed inset-0 z-[200] bg-black overflow-hidden ${
        phase === 8 ? 'opacity-0 pointer-events-none transition-opacity duration-[1500ms] ease-out' : 'opacity-100'
      }`}
      data-testid="intro-trailer"
    >
      <audio ref={(el) => (audioRefs.current.vo1 = el)} src="/assets/intro_vo_1.mp3" preload="auto" />
      <audio ref={(el) => (audioRefs.current.vo2 = el)} src="/assets/intro_vo_2.mp3" preload="auto" />
      <audio ref={(el) => (audioRefs.current.vo3 = el)} src="/assets/intro_vo_3.mp3" preload="auto" />

      {/* Grain overlay (persistent) */}
      <div className="pointer-events-none absolute inset-0 opacity-30 mix-blend-overlay z-30"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><filter id='n'><feTurbulence baseFrequency='0.95' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }} />
      {/* Scanlines */}
      <div className="pointer-events-none absolute inset-0 opacity-40 z-30 bg-[linear-gradient(transparent_0px,transparent_3px,rgba(255,255,255,0.03)_3px,rgba(255,255,255,0.03)_4px)] bg-[length:100%_4px]" />
      {/* Vignette */}
      <div className="pointer-events-none absolute inset-0 z-30"
        style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.75) 100%)' }} />
      {/* Flash */}
      <div className={`pointer-events-none absolute inset-0 bg-white z-40 transition-opacity duration-75 ${flash ? 'opacity-95' : 'opacity-0'}`} />
      {/* Letterbox bars */}
      <div className={`pointer-events-none absolute top-0 left-0 right-0 bg-black z-40 transition-all duration-700 ease-out ${letterbox && phase >= 0 && phase < 8 ? 'h-[8vh]' : 'h-0'}`} />
      <div className={`pointer-events-none absolute bottom-0 left-0 right-0 bg-black z-40 transition-all duration-700 ease-out ${letterbox && phase >= 0 && phase < 8 ? 'h-[8vh]' : 'h-0'}`} />

      {/* --- Gate --- */}
      {phase === -1 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 text-center px-6 pb-[env(safe-area-inset-bottom)]" data-testid="intro-gate">
          <p className="text-[10px] tracking-[0.5em] text-white/60 mb-6">SMG STUDIOS PRESENTS</p>
          <h2 className="font-display text-white text-[3.4rem] md:text-7xl leading-none mb-4">The Reel.</h2>
          <p className="text-white/50 text-[10px] tracking-[0.4em] mb-10">A CINEMATIC EXPERIENCE · 20 SECONDS</p>
          <div className="w-full max-w-sm md:max-w-none md:w-auto flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center gap-3 md:justify-center">
            <button onClick={() => handleGate({ muted: false })} data-testid="intro-play-sound"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#FF3D2E] hover:bg-[#ff5446] text-white text-[11px] tracking-[0.25em] transition-colors">
              <Volume2 className="w-4 h-4" /> PLAY WITH SOUND
            </button>
            <button onClick={() => handleGate({ muted: true })} data-testid="intro-play-muted"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-white/30 hover:border-white text-white text-[11px] tracking-[0.25em]">
              <VolumeX className="w-4 h-4" /> WATCH MUTED
            </button>
            <button onClick={handleSkip} data-testid="intro-skip-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 text-white/70 hover:text-white text-[11px] tracking-[0.25em]">
              SKIP <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Corner brackets */}
      {phase >= 0 && phase < 8 && (
        <>
          <span className="absolute top-[9vh] left-4 md:left-6 w-8 h-8 md:w-10 md:h-10 border-l border-t border-white/40 z-40" />
          <span className="absolute top-[9vh] right-4 md:right-6 w-8 h-8 md:w-10 md:h-10 border-r border-t border-white/40 z-40" />
          <span className="absolute bottom-[9vh] left-4 md:left-6 w-8 h-8 md:w-10 md:h-10 border-l border-b border-white/40 z-40" />
          <span className="absolute bottom-[9vh] right-4 md:right-6 w-8 h-8 md:w-10 md:h-10 border-r border-b border-white/40 z-40" />

          <div className="absolute top-[calc(8vh+16px)] left-6 md:left-10 right-6 md:right-10 flex items-center justify-between text-[10px] md:text-[11px] tracking-[0.3em] text-white z-40 mix-blend-difference">
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-[#FF3D2E] animate-pulse" />REC · SMG · 2026
            </div>
            <div className="hidden md:flex items-center gap-6 opacity-70">
              <span>REEL 01 — TAKE 07</span>
            </div>
          </div>
        </>
      )}

      {/* --- Phase 0: pre-roll (letterbox slides in, black hold) --- */}
      {phase === 0 && (
        <div className="absolute inset-0 z-10 bg-black" data-testid="intro-phase-preroll" />
      )}

      {/* --- Phase 1: COLD OPEN MONTAGE --- */}
      {phase === 1 && activeOpen && (
        <div key={`open-${openIdx}`} className="absolute inset-0 z-10 overflow-hidden" data-testid="intro-phase-open">
          <img src={activeOpen.src} alt="" draggable={false}
            className="absolute inset-0 w-full h-full object-cover animate-[kb_1200ms_ease-out_forwards]" />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute bottom-[calc(8vh+28px)] left-8 md:left-14 text-white z-20">
            <p className="text-[10px] md:text-[11px] tracking-[0.5em] opacity-80">{activeOpen.label}</p>
          </div>
        </div>
      )}

      {/* --- Phase 2: VO1 KINETIC MANIFESTO w/ background --- */}
      {phase === 2 && (
        <div className="absolute inset-0 z-10" data-testid="intro-phase-vo1">
          <img src={VO1_BG} alt="" draggable={false}
            className="absolute inset-0 w-full h-full object-cover scale-110 animate-[slowPan_5s_linear_forwards]" />
          <div className="absolute inset-0 bg-black/65" />
          <div className="absolute inset-0 flex items-center justify-center px-8 md:px-16">
            <span key={`s2-${slam}`}
              className="font-display text-white text-[11vw] md:text-[7.5vw] leading-[0.95] tracking-tight text-center animate-[shakeChroma_180ms_ease-out] max-w-[92vw] break-words"
              style={{ textShadow: '2px 0 rgba(255,61,46,0.55), -2px 0 rgba(0,180,255,0.4)' }}>
              {VO1_SLAMS[slam]?.text}
            </span>
          </div>
        </div>
      )}

      {/* Phase 3 render removed — themes now live inside Phase 4 as VO2 backdrops */}

      {/* --- Phase 4: VO2 MANIFESTO — theme backdrop cycles per slam --- */}
      {phase === 4 && (
        <div className="absolute inset-0 z-10" data-testid="intro-phase-vo2">
          {(() => {
            const bg = THEMES[slam]?.img || VO2_BG;
            const accent = THEMES[slam]?.accent || '#FF3D2E';
            const label = THEMES[slam]?.label;
            return (
              <>
                <img key={`vo2bg-${slam}`} src={bg} alt="" draggable={false}
                  className="absolute inset-0 w-full h-full object-cover scale-110 animate-[slowPan_2500ms_linear_forwards]" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accent}44 0%, rgba(0,0,0,0.72) 55%, ${accent}22 100%)` }} />
                {/* Tiny theme label bottom-left */}
                {label && (
                  <div className="absolute bottom-[calc(8vh+28px)] left-8 md:left-14 text-white z-20">
                    <p className="text-[10px] md:text-[11px] tracking-[0.5em] opacity-80">{label}</p>
                  </div>
                )}
                {/* Accent bar bottom-right */}
                <div className="absolute bottom-[calc(8vh+24px)] right-8 md:right-14 h-[3px] w-24 md:w-40 z-20" style={{ background: accent }} />
              </>
            );
          })()}
          <div className="absolute inset-0 flex items-center justify-center px-8 md:px-16 z-10">
            <span key={`s4-${slam}`}
              className="font-display text-white text-[8.5vw] md:text-[5.5vw] leading-[0.95] tracking-tight text-center animate-[shakeChroma_180ms_ease-out] max-w-[88vw] break-words"
              style={{ textShadow: '2px 0 rgba(255,61,46,0.55), -2px 0 rgba(0,180,255,0.4), 0 4px 30px rgba(0,0,0,0.85)' }}>
              {VO2_SLAMS[slam]?.text}
            </span>
          </div>
        </div>
      )}

      {/* --- Phase 5: AI · CULTURE · REVENUE with subliminal flashes --- */}
      {phase === 5 && triWord >= 0 && (
        <div key={`tri-${triWord}`} className="absolute inset-0 z-10 overflow-hidden animate-[punchIn_180ms_ease-out]" data-testid="intro-phase-tri">
          {/* Flash imagery behind */}
          {(() => {
            const key = ['AI', 'CULTURE', 'REVENUE'][triWord];
            const src = TRI_FLASHES[key][triFlash] || TRI_FLASHES[key][0];
            return (
              <img key={`flash-${triWord}-${triFlash}`} src={src} alt="" draggable={false}
                className="absolute inset-0 w-full h-full object-cover" />
            );
          })()}
          <div className="absolute inset-0" style={{ background: triWord === 1 ? 'rgba(0,0,0,0.55)' : 'rgba(255,61,46,0.45)' }} />
          <div className="absolute inset-0 flex items-center justify-center px-8 md:px-16">
            <span className={`font-display leading-[0.95] tracking-tight animate-[shakeChroma_180ms_ease-out] text-white max-w-[92vw] text-center ${
              triWord === 2 ? 'text-[20vw] md:text-[15vw]' : triWord === 1 ? 'text-[22vw] md:text-[17vw]' : 'text-[24vw] md:text-[19vw]'
            }`}
              style={{ textShadow: '3px 0 rgba(0,0,0,0.55), -3px 0 rgba(255,255,255,0.25), 0 0 40px rgba(0,0,0,0.75)' }}>
              {['AI.', 'CULTURE.', 'REVENUE.'][triWord]}
            </span>
          </div>
        </div>
      )}

      {/* --- Phase 6: LOGO CLIMAX --- */}
      {phase === 6 && (
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-black" data-testid="intro-phase-logo">
          <span className="absolute w-[90vw] h-[90vw] max-w-[1000px] max-h-[1000px] rounded-full blur-3xl opacity-70 animate-[glowPulse_1200ms_ease-out]"
            style={{ background: 'radial-gradient(closest-side, rgba(255,61,46,0.65), rgba(120,30,160,0.25) 45%, transparent 75%)' }} />
          <div className="relative flex flex-col items-center animate-[punchInBig_900ms_cubic-bezier(0.22,1,0.36,1)]">
            <img src={SMG_LOGO} alt="SMG Media Group" draggable={false}
              className="w-[45vw] max-w-[560px] object-contain drop-shadow-[0_0_50px_rgba(255,61,46,0.7)]" />
            <p className="mt-6 text-[10px] md:text-[13px] tracking-[0.6em] text-white/90">MEDIA GROUP</p>
          </div>
        </div>
      )}

      {/* --- Phase 7: FINALE CARD --- */}
      {phase === 7 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-black text-center px-6 animate-[fadeUp_500ms_ease-out]" data-testid="intro-phase-finale">
          <img src={SMG_LOGO} alt="" draggable={false} className="w-[22vw] max-w-[280px] object-contain mb-8" />
          <div className="flex flex-wrap items-baseline justify-center gap-x-6 md:gap-x-10">
            <span className="font-display text-white text-4xl md:text-6xl lg:text-7xl tracking-tight">AI.</span>
            <span className="font-display text-[#FF3D2E] text-4xl md:text-6xl lg:text-7xl tracking-tight">CULTURE.</span>
            <span className="font-display text-white text-4xl md:text-6xl lg:text-7xl tracking-tight">REVENUE.</span>
          </div>
          <p className="mt-8 text-[10px] md:text-[11px] tracking-[0.5em] text-white/70">WHERE BRANDS BECOME MOVEMENTS</p>
        </div>
      )}

      {/* Bottom bar */}
      {phase >= 0 && phase < 8 && (
        <div className="absolute bottom-[calc(8vh+16px)] left-6 md:left-10 right-6 md:right-10 z-40 mix-blend-difference text-white">
          <div className="flex items-center justify-between text-[10px] md:text-[11px] tracking-[0.3em]">
            <span className="font-mono tabular-nums">{tc}</span>
            <button onClick={handleSkip} data-testid="intro-skip-btn"
              className="group inline-flex items-center gap-2 px-4 py-2 border border-white/40 hover:border-white transition-colors">
              SKIP INTRO <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes punchIn { 0%{transform:scale(1.15);opacity:0} 60%{opacity:1} 100%{transform:scale(1);opacity:1} }
        @keyframes punchInBig { 0%{transform:scale(2.4);opacity:0;filter:blur(20px)} 55%{transform:scale(0.94);opacity:1;filter:blur(0)} 78%{transform:scale(1.03)} 100%{transform:scale(1)} }
        @keyframes shakeChroma { 0%{transform:translate(0,0)} 20%{transform:translate(-5px,2px)} 40%{transform:translate(4px,-3px)} 60%{transform:translate(-2px,4px)} 80%{transform:translate(2px,-1px)} 100%{transform:translate(0,0)} }
        @keyframes glowPulse { 0%{transform:scale(0.5);opacity:0} 55%{transform:scale(1.15);opacity:1} 100%{transform:scale(1);opacity:0.7} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes kb { 0%{transform:scale(1.05) translate(0,0)} 100%{transform:scale(1.18) translate(-1.5%,1.5%)} }
        @keyframes slowPan { 0%{transform:scale(1.1) translate(0,0)} 100%{transform:scale(1.18) translate(-2%,-1%)} }
      `}</style>
    </div>
  );
}
